import React, { useMemo, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { DatePicker, Form, Input, InputNumber, InputNumberProps, Select } from 'antd';
import moment from 'moment';

import { moneyFormatter } from '@utils';
import { CreditBureauReport } from '@pages/credit-risk/analysis/components/CreditBureau';

import cls from './credit-bureau-form.module.css';

const lenderOptions = [
  { label: 'Verqor', value: 'Verqor' },
  { label: 'Banco', value: 'Banco' },
  { label: 'Otro', value: 'Otro' },
];

const productOptions = [
  { label: 'Avio', value: 'Avio' },
  { label: 'TDC', value: 'TDC' },
  { label: 'Credito Auto', value: 'Credito Auto' },
  { label: 'Renta', value: 'Arrendamiento' },
  { label: 'Simple', value: 'Simple' },
  { label: 'Hipoteca', value: 'Hipoteca' },
  { label: 'LDC', value: 'LDC' },
  { label: 'Factoraje', value: 'Factoraje' },
  { label: 'Reestructurados', value: 'Reestructurados' },
  { label: 'Personal', value: 'Personal' },
  { label: 'Renta', value: 'Renta' },
];

const dpdOptions = [
  { label: '–', value: null },
  { label: '1-30', value: '1-30' },
  { label: '31-60', value: '31-60' },
  { label: '61-90', value: '61-90' },
  { label: '91-120', value: '91-120' },
  { label: '121-180', value: '121-180' },
  { label: '180+', value: '180+' },
];

const TERM_MAX_DAYS = 9999;
const MAX_MONEY_VALUE = 1_000_000_000;
const MAX_INVOICE_COUNT = 1000000;

const calculateTotalSum = (rows, name) => (rows ?? []).reduce(
  (sum, row) => sum + (Number(row?.[name] || 0)),
  0,
);

const MoneyInput = (props: InputNumberProps<number>) => {
  return (
    <InputNumber
      {...props}
      min={0}
      max={MAX_MONEY_VALUE}
      placeholder="–"
      controls={false}
      formatter={(value: any) => {
        if (value === null || value === undefined || value === '') return '';
        const num = Number(value);
        if (Number.isNaN(num) || num === 0) return '';
        return `$ ${num}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      }}
      parser={(value: any) => {
        const rawValue = String(value ?? '').trim();
        if (!rawValue) return 0;
        const num = Number(rawValue.replace(/\$\s?|(,*)/g, ''));
        return Number.isNaN(num) ? 0 : num;
      }}
      onFocus={evt => {
        setTimeout(() => {
          if (evt.target.value?.length <= 3) {
            evt.target.selectionStart = evt.target.selectionEnd = 10000;
          }
        }, 0);
      }}
      className={cls.input}
    />
  );
};

interface Props {
  creditBureau: CreditBureauReport;
  creditTermValue: number;
  onChange?: (next: CreditBureauReport) => void;
  annualRevenueValue?: number;
  noiRateValue?: number;
  creditBureauAnualRevenue: number | null;
}

export const CreditBureauForm = ({
  creditBureau, creditTermValue, onChange, annualRevenueValue, noiRateValue,
  creditBureauAnualRevenue,
}: Props) => {
  const [form] = Form.useForm();
  const lastEmittedSnapshotRef = useRef<string>('');

  const calculateValues = (values: any, serializeDates = false): CreditBureauReport => {
    const rows = (values?.rows || []).map((row: any) => {
      const totalAmount = Number(row?.totalAmount || 0);
      const capitalBalance = Number(row?.capitalBalance || 0);
      const overdueBalance = Number(row?.overdueBalance || 0);

      const dscrPercent = totalAmount > 0
        ? Math.round(((capitalBalance + overdueBalance) / totalAmount) * 100)
        : null;

      const totalPayment = Number(row?.totalPayment || 0);
      const fullPayment = totalPayment * creditTermValue > totalAmount
        ? totalAmount
        : totalPayment * creditTermValue;

      const openingDateValue = serializeDates
        ? (row?.openingDate ? moment(row.openingDate).toISOString() : null)
        : (row?.openingDate ? moment(row.openingDate) : undefined);

      return {
        ...row,
        openingDate: openingDateValue,
        term: row?.term === '' || row?.term === null || row?.term === undefined ? null : Number(row.term),
        fullPayment,
        dscr: dscrPercent,
      };
    });

    const totalAmountSum = calculateTotalSum(rows, 'totalAmount');
    const capitalBalanceSum = calculateTotalSum(rows, 'capitalBalance');
    const overdueBalanceSum = calculateTotalSum(rows, 'overdueBalance');
    const monthlyPaymentSum = calculateTotalSum(rows, 'monthlyPayment');
    const latePaymentSum = calculateTotalSum(rows, 'latePayment');
    const totalPaymentSum = calculateTotalSum(rows, 'totalPayment');
    const fullPaymentSum = calculateTotalSum(rows, 'fullPayment');

    const annualRevenue = Number(
      annualRevenueValue
      ?? values?.annualRevenue
      ?? creditBureau.annualRevenue
      ?? 0,
    );
    const noiRate = Number(noiRateValue ?? values?.noiRate ?? creditBureau.noiRate ?? 0);
    const invoices = Number(values?.invoices ?? creditBureau.invoices ?? 0);

    const monthlySalesRevenue = annualRevenue / 12;
    const monthlyNoi = +((annualRevenue * noiRate / 100) / 12).toFixed(2);
    const avgInvoice = invoices > 0 ? annualRevenue / invoices : 0;

    const guaranteeSuggestion = (() => {
      const ratio = totalPaymentSum > 0 ? (monthlyNoi / totalPaymentSum) : 0;

      if (ratio < 1) return Math.max(0.4 * monthlySalesRevenue, (1 - ratio) * totalPaymentSum * 2);
      if (ratio < 1.5) return Math.max(0.8 * annualRevenue, (1.5 - ratio) * totalPaymentSum * 2);

      return 0;
    })();

    return {
      ...creditBureau,
      ...values,
      rows,
      annualRevenue,
      noiRate,
      invoices,
      monthlySalesRevenue,
      monthlyNoi,
      avgInvoice,
      guaranteeSuggestion,
      total: {
        totalAmount: totalAmountSum,
        capitalBalance: capitalBalanceSum,
        overdueBalance: overdueBalanceSum,
        monthlyPayment: monthlyPaymentSum,
        latePayment: latePaymentSum,
        totalPayment: totalPaymentSum,
        fullPayment: fullPaymentSum,
      },
    };
  };

  const initialValues = useMemo(() => {
    return calculateValues(creditBureau, false);
  }, [creditBureau, annualRevenueValue, noiRateValue, creditTermValue]);

  const emitIfChanged = (next: CreditBureauReport) => {
    if (!onChange) return;

    const snapshot = JSON.stringify(next);
    if (lastEmittedSnapshotRef.current === snapshot) return;

    lastEmittedSnapshotRef.current = snapshot;
    onChange(next);
  };

  const emitToParent = () => {
    const values = form.getFieldsValue(true);
    emitIfChanged(calculateValues(values, true));
  };

  useEffect(() => {
    try {
      form.setFieldsValue(initialValues);
      emitIfChanged(calculateValues(initialValues, true));
    } catch (e) {
      console.error(e);
    }
  }, [form, initialValues, onChange]);

  return (
    <div className={cls._}>
      <Form
        form={form}
        className={cls.form}
        onValuesChange={emitToParent}
      >
        <div className={cls.head}>
          <div className={cls.headItem}>Lender</div>
          <div className={cls.headItem}>Product</div>
          <div className={cls.headItem}>Opening Date</div>
          <div className={cls.headItem}>Term (days)</div>
          <div className={cls.headItem}>Total Amount</div>
          <div className={cls.headItem}>Capital Balance</div>
          <div className={cls.headItem}>Overdue Balance</div>
          <div className={cls.headItem}>Bucket DPD</div>
          <div className={cls.headItem}>Monthly Payment</div>
          <div className={cls.headItem}>Late Payment</div>
          <div className={cls.headItem}>Total Payment</div>
          <div className={cls.headItem}>Full Payment</div>
          <div className={cls.headItem}>DSCR Y/N</div>
        </div>

        <Form.List name="rows">
          {(fields, { add, remove }) => (
            <div className={cls.rows}>
              {fields.map(({ key, name, ...restField }) => (
                <div key={key} className={cls.row}>
                  <Form.Item
                    {...restField}
                    name={[name, 'lender']}
                    className={cls.item}
                  >
                    <Select options={lenderOptions} className={cls.select} />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'product']}
                    className={cls.item}
                  >
                    <Select options={productOptions} className={cls.select} />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'openingDate']}
                    className={cls.item}
                  >
                    <DatePicker
                      placeholder="–"
                      className={cls.datepicker}
                      format="DD/MM/YY"
                      suffixIcon={null}
                    />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'term']}
                    className={cls.item}
                    getValueFromEvent={(e) => {
                      const value = e?.target?.value.replace(/\D/g, '');
                      return value < TERM_MAX_DAYS ? value : TERM_MAX_DAYS;
                    }}
                  >
                    <Input
                      placeholder="–"
                      inputMode="numeric"
                      pattern="[0-9]*"
                    />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'totalAmount']}
                    className={cls.item}
                  >
                    <MoneyInput />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'capitalBalance']}
                    className={cls.item}
                  >
                    <MoneyInput />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'overdueBalance']}
                    className={cls.item}
                  >
                    <MoneyInput />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'bucketDpd']}
                    className={cls.item}
                  >
                    <Select options={dpdOptions} className={cls.select} />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'monthlyPayment']}
                    className={cls.item}
                  >
                    <MoneyInput />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'latePayment']}
                    className={cls.item}
                  >
                    <MoneyInput />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'totalPayment']}
                    className={cls.item}
                  >
                    <MoneyInput />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'fullPayment']}
                    className={cls.item}
                  >
                    <Form.Item noStyle shouldUpdate>
                      {({ getFieldValue }) => {
                        const value = getFieldValue(['rows', name, 'fullPayment']);
                        return (
                          <div className={cls.itemValue}>
                            {value ? moneyFormatter.format(value) : '–'}
                          </div>
                        );
                      }}
                    </Form.Item>
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'dscr']}
                    className={cls.item}
                  >
                    <Form.Item noStyle shouldUpdate>
                      {({ getFieldValue }) => {
                        const totalAmount = Number(getFieldValue(['rows', name, 'totalAmount']) || 0);
                        const capitalBalance = Number(getFieldValue(['rows', name, 'capitalBalance']) || 0);
                        const overdueBalance = Number(getFieldValue(['rows', name, 'overdueBalance']) || 0);

                        if (totalAmount <= 0) return <div className={cls.itemValue}>–</div>;

                        const percent = Math.round(
                          ((capitalBalance + overdueBalance) / totalAmount) * 100,
                        );

                        return <div className={cls.itemValue}>{percent}%</div>;
                      }}
                    </Form.Item>
                  </Form.Item>
                  <div className={cls.remove}>
                    <button className={cls.removeButton} type="button" onClick={() => remove(name)}>
                      <svg xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none">
                        <path d="M7 0C10.866 0 14 3.13401 14 7C14 10.866 10.866 14 7 14C3.13401 14 0 10.866 0 7C0 3.13401 3.13401 0 7 0ZM9.9502 4.29297C9.55967 3.90244 8.92568 3.90244 8.53516 4.29297L7.12109 5.70605L5.70703 4.29297C5.31651 3.90244 4.68349 3.90244 4.29297 4.29297C3.90244 4.68349 3.90244 5.31651 4.29297 5.70703L5.70605 7.12109L4.29297 8.53516C3.90244 8.92568 3.90244 9.55967 4.29297 9.9502C4.68343 10.3404 5.31657 10.3404 5.70703 9.9502L7.12109 8.53516L8.53516 9.9502C8.92568 10.3407 9.55968 10.3407 9.9502 9.9502C10.3407 9.55968 10.3407 8.92568 9.9502 8.53516L8.53516 7.12109L9.9502 5.70703C10.3404 5.31657 10.3404 4.68343 9.9502 4.29297Z"
                          fill="#D32F2F" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
              <Form.Item className={cls.add}>
                <button className={cls.addButton} type="button" onClick={() => add()}>
                  <svg xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none">
                    <path d="M12 0C16.4183 0 20 3.58172 20 8V12C20 16.4183 16.4183 20 12 20H8C3.58172 20 6.44277e-08 16.4183 0 12V8C0 3.58172 3.58172 6.44256e-08 8 0H12ZM10 6C9.44772 6 9 6.44772 9 7V9H7C6.44772 9 6 9.44772 6 10C6 10.5523 6.44772 11 7 11H9V13C9 13.5523 9.44772 14 10 14C10.5523 14 11 13.5523 11 13V11H13C13.5523 11 14 10.5523 14 10C14 9.44772 13.5523 9 13 9H11V7C11 6.44772 10.5523 6 10 6Z"
                      fill="#042E6B" />
                  </svg>
                </button>
              </Form.Item>
            </div>
          )}
        </Form.List>
        <div className={clsx(cls.row, cls.total)}>
          <div className={cls.item}>Total</div>
          <div className={cls.item}>
            {creditBureau.rows.length === 0
              ? '–'
              : `${creditBureau.rows.length} credit${creditBureau.rows.length > 1
                ? 's'
                : ''}`
            }
          </div>
          <div className={clsx(cls.item, cls.hl)} />
          <div className={clsx(cls.item, cls.hl)} />
          <Form.Item
            name={['total', 'totalAmount']}
            className={cls.item}
          >
            <div>{moneyFormatter.format(creditBureau?.total?.totalAmount || 0)}</div>
          </Form.Item>

          <Form.Item
            name={['total', 'capitalBalance']}
            className={cls.item}
          >
            <div>{moneyFormatter.format(creditBureau?.total?.capitalBalance || 0)}</div>
          </Form.Item>

          <Form.Item
            name={['total', 'overdueBalance']}
            className={cls.item}
          >
            <div>{moneyFormatter.format(creditBureau?.total?.overdueBalance || 0)}</div>
          </Form.Item>

          <div className={clsx(cls.item, cls.hl)} />

          <Form.Item
            name={['total', 'monthlyPayment']}
            className={cls.item}
          >
            <div>{moneyFormatter.format(creditBureau?.total?.monthlyPayment || 0)}</div>
          </Form.Item>

          <Form.Item
            name={['total', 'latePayment']}
            className={cls.item}
          >
            <div>{moneyFormatter.format(creditBureau?.total?.latePayment || 0)}</div>
          </Form.Item>

          <Form.Item
            name={['total', 'totalPayment']}
            className={cls.item}
          >
            <div>{moneyFormatter.format(creditBureau?.total?.totalPayment || 0)}</div>
          </Form.Item>

          <Form.Item
            name={['total', 'fullPayment']}
            className={cls.item}
          >
            <div>{moneyFormatter.format(creditBureau?.total?.fullPayment || 0)}</div>
          </Form.Item>

          <div className={clsx(cls.item, cls.hl)} />
        </div>

        <div className={cls.footer}>
          <div className={cls.left}>
            <div className={cls.footerGroup}>
              <div className={cls.footerItem}>
                <div className={cls.footerLabel}>Annual Revenue</div>
                <Form.Item name="annualRevenue" className={cls.footerValue}>
                  <div className={cls.footerValueText}>
                    {moneyFormatter.format(creditBureauAnualRevenue ?? 0)}
                  </div>
                </Form.Item>
              </div>
            </div>

            <div className={cls.footerGroup}>
              <div className={cls.footerItem}>
                <div className={cls.footerLabel}>Monthly Sales Revenue</div>
                <Form.Item name="monthlySalesRevenue" className={cls.footerValue}>
                  <div className={cls.footerValueText}>
                    {moneyFormatter.format(creditBureau.monthlySalesRevenue || 0)}
                  </div>
                </Form.Item>
              </div>

              <div className={cls.footerItem}>
                <div className={cls.footerLabel}>NOI Rate</div>
                <Form.Item name="noiRate" className={cls.footerValue}>
                  <div className={cls.footerValueText}>{creditBureau.noiRate || 0}%</div>
                </Form.Item>
              </div>

              <div className={cls.footerItem}>
                <div className={cls.footerLabel}>Monthly NOI</div>
                <Form.Item name="monthlyNoi" className={cls.footerValue}>
                  <div className={cls.footerValueText}>
                    {moneyFormatter.format(creditBureau.monthlyNoi || 0)}
                  </div>
                </Form.Item>
              </div>
            </div>
          </div>
          <div className={cls.right}>
            <div className={cls.footerGroup}>
              <div className={cls.footerItem}>
                <div className={cls.footerLabel}>Invoices</div>
                <Form.Item
                  name="invoices"
                  className={cls.footerValue}
                  getValueFromEvent={(e) => {
                    const digits = String(e?.target?.value ?? '').replace(/\D/g, '');
                    return digits ? String(Math.min(Number(digits), MAX_INVOICE_COUNT)) : '';
                  }}
                >
                  <Input
                    className={clsx(cls.footerValueInput, cls.hl)}
                    placeholder="–"
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                </Form.Item>
              </div>

              <div className={cls.footerItem}>
                <div className={cls.footerLabel}>AVG Invoice</div>
                <Form.Item name="averageInvoice" className={cls.footerValue}>
                  <div className={cls.footerValueText}>
                    {moneyFormatter.format(creditBureau.avgInvoice || 0)}
                  </div>
                </Form.Item>
              </div>
            </div>

            <div className={cls.footerGroup}>
              <div className={cls.footerItem}>
                <div className={cls.footerLabel}>Guarantee</div>
                <Form.Item
                  name="guarantee"
                  className={cls.footerValue}
                >
                  <InputNumber
                    min={0}
                    max={1000000000}
                    formatter={(value) => `$${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={(value) => value ? +value.replace(/\$\s?|(,*)/g, '') : 0}
                    defaultValue={form.getFieldValue('guarantee')}
                    bordered={false}
                    onChange={val => form.setFieldValue('guarantee', val ? +val : 0)}
                    className={clsx(cls.input, cls.hl)}
                    controls={false}
                    maxLength={15}
                  />
                </Form.Item>
              </div>
              <div className={cls.footerItem}>
                <div className={cls.footerLabel}>Guarantee Suggestion</div>
                <Form.Item name="guaranteeSuggestion" className={cls.footerValue}>
                  <div className={cls.footerValueText}>
                    {moneyFormatter.format(creditBureau.guaranteeSuggestion || 0)}
                  </div>
                </Form.Item>
              </div>
            </div>
          </div>
        </div>
      </Form>
    </div>
  );
};
